import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Input, Select, Space, Table, Tag, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, ExportOutlined, ImportOutlined, UploadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  apiErrorMessage,
  COMPETENCY_PERMS as P,
  competencyKeys,
  useCompetencyCan,
  useEffectivenessList,
} from '@/hooks/useCompetency';
import { competencyService, type EffectivenessEvaluation } from '@/services/competency.service';
import { triggerDownload } from '@/reports/utils/exportTable';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, STATUS_COLOR } from './competencyDisplay';

const EffectivenessListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canList = useCompetencyCan(P.trainingView, P.view, P.effectivenessEvaluate, P.trainingManage);
  const canImport = useCompetencyCan(P.import);
  const canExport = canImport || canList;
  const [status, setStatus] = useState<string | undefined>('Open');
  const [overdue, setOverdue] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<'template' | 'export' | 'import' | null>(null);
  const { data = [], isLoading, refetch } = useEffectivenessList(
    { status, overdue: overdue ? '1' : undefined, search },
    canList
  );

  const columns: ColumnsType<EffectivenessEvaluation> = [
    {
      title: 'Session',
      dataIndex: 'session_number',
      width: 150,
      render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/effectiveness/${r.id}`)}>{v}</a>,
    },
    { title: 'Training Date', dataIndex: 'session_date', width: 120, render: (v) => formatDate(v) },
    {
      title: 'Training Topic',
      render: (_, r) => [r.topic, `${r.course_code ?? ''} ${r.course_title}`.trim()].filter(Boolean).join(' — '),
    },
    { title: 'Trainer Name', dataIndex: 'trainer_display', width: 170 },
    { title: 'Trainees', width: 100, align: 'right', render: (_, r) => `${r.scored_count ?? 0} / ${r.trainee_count ?? 0}` },
    {
      title: 'Due',
      dataIndex: 'due_date',
      width: 130,
      render: (v, r) => (
        <Space size={4}>
          {formatDate(v)}
          {r.overdue && <Tag color="red">Overdue</Tag>}
        </Space>
      ),
    },
    { title: 'Status', dataIndex: 'status', width: 120, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    {
      title: 'Overall effective',
      dataIndex: 'overall_effective',
      width: 120,
      render: (v, r) => (r.status === 'Open' ? '-' : Number(v) ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
  ];

  const downloadTemplate = async () => {
    setBusy('template');
    try {
      triggerDownload(
        await competencyService.downloadImportTemplate('effectiveness'),
        'competency-effectiveness-template.xlsx'
      );
      message.success('Template downloaded');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const exportCurrent = async () => {
    setBusy('export');
    try {
      triggerDownload(await competencyService.exportEffectiveness(), 'training-effectiveness-export.xlsx');
      message.success('Export started');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const importFile = async (file: File) => {
    setBusy('import');
    try {
      const check = await competencyService.runImport('effectiveness', file, true);
      if (check.errors.length) {
        message.error(
          `Check failed: ${check.errors[0].message}${check.errors.length > 1 ? ` (+${check.errors.length - 1} more)` : ''}`
        );
        navigate(`${COMPETENCY_BASE}/import?kind=effectiveness`);
        return false;
      }
      const result = await competencyService.runImport('effectiveness', file, false);
      message.success(`Imported: ${result.created} created, ${result.updated} updated`);
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      void refetch();
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
    return false;
  };

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title="Training effectiveness"
        extra={
          <Space wrap>
            {canExport ? (
              <Button icon={<ExportOutlined />} loading={busy === 'export'} onClick={() => void exportCurrent()}>
                Export
              </Button>
            ) : null}
            {canImport ? (
              <>
                <Button icon={<DownloadOutlined />} loading={busy === 'template'} onClick={() => void downloadTemplate()}>
                  Template
                </Button>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={(f) => {
                    void importFile(f);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={busy === 'import'}>
                    Import
                  </Button>
                </Upload>
                <Link to={`${COMPETENCY_BASE}/import?kind=effectiveness`}>
                  <Button icon={<ImportOutlined />}>Bulk import wizard</Button>
                </Link>
              </>
            ) : null}
          </Space>
        }
      />
      {!canList && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Evaluations assigned to you as line manager or trainer open from your notifications and task list."
        />
      )}
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search placeholder="Search session, course or trainer" allowClear style={{ width: 280 }} onSearch={setSearch} />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={['Open', 'Completed', 'Acknowledged'].map((s) => ({ value: s, label: s }))}
          />
          <Checkbox checked={overdue} onChange={(e) => setOverdue(e.target.checked)}>
            Overdue only
          </Checkbox>
        </Space>
        <Table rowKey="id" size="small" loading={isLoading} dataSource={data} columns={columns} pagination={{ defaultPageSize: 20 }} />
      </Card>
    </div>
  );
};

export default EffectivenessListPage;
